/**
 * La faune sur le fil.
 *
 * Le serveur simule les quatre cents animaux une seule fois, et chaque joueur
 * doit voir LES MEMES. Envoyer trois cents objets JSON dix fois par seconde
 * ferait 300 kbit/s par joueur pour des betes dont la moitie sont hors de
 * l'ecran: on n'envoie donc que le VOISINAGE de chacun, et sous une forme
 * compacte.
 *
 * FORME. Un tableau plat d'entiers, cinq par animal, sans noms de champs:
 * l'identifiant, x et z en centimetres, le cap en centiemes de radian, et le
 * rang de l'etat. En JSON, un animal coute ainsi une trentaine d'octets au lieu
 * de cent-vingt. Ce qui manque (la hauteur, la couleur, la taille) n'a pas
 * besoin de voyager: le client le recalcule depuis le terrain et la graine, qui
 * sont les memes partout.
 *
 * CE QUI N'EST PAS ENVOYE NE MANQUE PAS. Un animal hors du rayon garde sa
 * derniere position connue chez le client. Il est a quatre-vingts metres, la
 * camera ne le montre pas, et il reprendra vie des qu'on s'en approchera.
 *
 * Module PUR: il sert a l'encodage cote serveur et au decodage cote client.
 */
import type { Animal, EtatAnimal } from '../types/monde.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'
import { yAnimal } from './animaux.ts'

/**
 * Rayon d'interet autour de chaque joueur, en metres.
 *
 * La camera orthographique montre une quarantaine de metres autour du
 * bonhomme; quatre-vingts laissent de la marge pour un zoom arriere et pour le
 * temps qu'un animal met a entrer dans le champ.
 */
export const RAYON_FAUNE = 80

/**
 * Plafond d'animaux envoyes en une trame.
 *
 * C'est un GARDE-FOU de debit, pas une limite d'affichage: dans une clairiere
 * ordinaire il y a une vingtaine de betes a portee. Le plafond ne sert qu'a
 * empecher qu'un joueur pose au milieu d'un banc de poissons ne fasse tripler
 * le trafic de tout le monde.
 */
export const MAX_FAUNE = 90

/** Periode de diffusion de la faune, en millisecondes. */
export const PERIODE_FAUNE_MS = 125

/** Nombre d'entiers par animal dans la trame. */
export const CHAMPS_FAUNE = 5

/**
 * Ordre des etats sur le fil. Une TABLE, pas une liste: le compilateur exige
 * une entree par valeur de `EtatAnimal`, donc ajouter un etat sans lui donner
 * de rang ne compile pas. Les rangs existants ne doivent jamais changer.
 */
const RANG_ETAT: Record<EtatAnimal, number> = {
  errance: 0,
  broute: 1,
  fuite: 2,
  dort: 3,
  suit: 4,
  troupeau: 5,
  boule: 6,
  garde: 7,
  chasse: 8,
  nage: 9,
  plane: 10,
}

const ETAT_PAR_RANG: readonly EtatAnimal[] = [
  'errance',
  'broute',
  'fuite',
  'dort',
  'suit',
  'troupeau',
  'boule',
  'garde',
  'chasse',
  'nage',
  'plane',
]

/**
 * Encode les animaux les plus proches de (x, z).
 *
 * Le tri est fait sur le carre de la distance et seulement sur les candidats
 * deja retenus par le rayon: en pratique une vingtaine d'elements, ce qui rend
 * le tri moins cher que le balayage lui-meme.
 */
export function encoderFaune(animaux: readonly Animal[], x: number, z: number): number[] {
  const r2 = RAYON_FAUNE * RAYON_FAUNE
  const proches: { i: number; d2: number }[] = []
  for (let i = 0; i < animaux.length; i++) {
    const a = animaux[i] as Animal
    const dx = a.pos.x - x
    const dz = a.pos.z - z
    const d2 = dx * dx + dz * dz
    if (d2 <= r2) proches.push({ i, d2 })
  }
  if (proches.length > MAX_FAUNE) {
    proches.sort((p, q) => p.d2 - q.d2)
    proches.length = MAX_FAUNE
  }
  const trame: number[] = []
  for (const { i } of proches) {
    const a = animaux[i] as Animal
    trame.push(
      a.id,
      Math.round(a.pos.x * 100),
      Math.round(a.pos.z * 100),
      Math.round(a.rot * 100),
      RANG_ETAT[a.etat],
    )
  }
  return trame
}

/** Une position d'animal telle qu'elle sort du fil. */
export interface AnimalRecu {
  x: number
  z: number
  rot: number
  etat: EtatAnimal
}

/**
 * Constante de temps du lissage, en secondes.
 *
 * Les trames arrivent huit fois par seconde et le rendu en veut soixante:
 * poser l'animal la ou la derniere trame l'a dit, c'est le teleporter huit fois
 * par seconde, et le moteur de rendu deduit la vitesse d'animation de l'ecart
 * entre deux images — un animal teleporte se met a galoper sur place. Quatre-
 * vingts millisecondes rattrapent l'essentiel de l'ecart entre deux trames sans
 * ajouter de retard visible.
 */
const TAU_FAUNE = 0.08

/**
 * Ecart au-dela duquel on ne lisse plus, en metres. Un animal qu'on retrouve
 * en s'approchant a pu vivre trente secondes sans qu'on l'ecoute: le trainer
 * lentement jusqu'a sa vraie place ferait traverser la clairiere a un lapin.
 */
const SAUT_FAUNE = 8

/** Interpolation d'angle par le plus court chemin. */
function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2)
  if (d > Math.PI) d -= Math.PI * 2
  if (d < -Math.PI) d += Math.PI * 2
  return a + d * t
}

/**
 * La faune telle que le serveur la voit, cote client.
 *
 * On garde la DERNIERE position connue de chaque animal et on y amene le
 * modele local en douceur. Aucune extrapolation: inventer un mouvement qui n'a
 * pas eu lieu fait avancer une bete immobile puis la ramene en arriere, et sur
 * un troupeau entier cela se voit tout de suite.
 */
export class SuiviFaune {
  private readonly cibles = new Map<number, AnimalRecu>()

  /** Nombre d'animaux dont on a des nouvelles. Sert aux mesures et aux tests. */
  get nombre(): number {
    return this.cibles.size
  }

  /** Range une trame recue. Une trame mal taillee est ignoree, pas fatale. */
  recevoir(trame: readonly number[]): number {
    let recus = 0
    for (let k = 0; k + CHAMPS_FAUNE <= trame.length; k += CHAMPS_FAUNE) {
      const id = trame[k] as number
      const etat = ETAT_PAR_RANG[trame[k + 4] as number]
      if (etat === undefined) continue
      let cible = this.cibles.get(id)
      if (!cible) {
        cible = { x: 0, z: 0, rot: 0, etat }
        this.cibles.set(id, cible)
      }
      cible.x = (trame[k + 1] as number) / 100
      cible.z = (trame[k + 2] as number) / 100
      cible.rot = (trame[k + 3] as number) / 100
      cible.etat = etat
      recus++
    }
    return recus
  }

  /** Les animaux dont on a des nouvelles, tries. Passerelle de test. */
  ids(): number[] {
    return [...this.cibles.keys()].sort((a, b) => a - b)
  }

  /** La derniere position connue d'un animal, ou null. Passerelle de test. */
  positionDe(id: number): Vec2 | null {
    const c = this.cibles.get(id)
    return c ? { x: c.x, z: c.z } : null
  }

  vider(): void {
    this.cibles.clear()
  }

  /**
   * Amene les animaux locaux vers ce que le serveur a dit.
   *
   * `phase` avance toujours, meme pour une bete dont on n'a pas de nouvelles:
   * c'est elle qui fait battre les ailes et respirer les dormeurs. La faire
   * dependre du reseau ferait figer un papillon en plein vol.
   */
  appliquer(animaux: Animal[], dt: number, terrain: ITerrain): void {
    const f = 1 - Math.exp(-dt / TAU_FAUNE)
    for (const a of animaux) {
      a.phase += dt
      const c = this.cibles.get(a.id)
      if (!c) continue
      const dx = c.x - a.pos.x
      const dz = c.z - a.pos.z
      if (dx * dx + dz * dz > SAUT_FAUNE * SAUT_FAUNE) {
        a.pos.x = c.x
        a.pos.z = c.z
        a.rot = c.rot
      } else {
        a.pos.x += dx * f
        a.pos.z += dz * f
        a.rot = lerpAngle(a.rot, c.rot, f)
      }
      a.etat = c.etat
      a.y = yAnimal(terrain, a.kind, a.etat, a.pos.x, a.pos.z)
    }
  }
}
